<template>
  <section
    v-if="faqList.length"
    itemscope
    itemtype="https://schema.org/FAQPage"
    class="container my-24 px-6 sm:mx-auto lg:mb-32 lg:mt-40 lg:max-w-7xl"
  >
    <IntlMsg
      class="mb-6 text-4xl font-bold leading-none tracking-tight sm:mb-10"
      v-bind="{ k: 'common.faq', tag: 'h2' }"
    />

    <div class="grid gap-4">
      <AppFaqItem v-for="(item, i) in faqList" :key="i" v-bind="{ item }" />
    </div>
  </section>

  <article
    v-if="footerMarkupRender || footerFallbackMarkupRender"
    class="c-footer-content container mx-auto my-24 px-6 leading-relaxed text-gray-600 lg:max-w-7xl"
  >
    <figure
      class="relative z-1 mb-16 min-h-64 w-full rounded-4xl bg-gray-250 text-gray-900 sm:min-h-96"
    >
      <figure
        class="absolute inset-0 rounded-inherit bg-gradient-to-b from-ice-300 to-gray-200"
      ></figure>
      <img
        :src="img2"
        alt=""
        loading="lazy"
        class="absolute left-0 top-0 h-full w-full rounded-inherit object-cover object-top"
      />
    </figure>
    <div
      class="px-6 lg:px-0"
      v-html="footerMarkupRender || footerFallbackMarkupRender"
    />
  </article>
</template>

<script lang="ts" setup>
import { computed, onServerPrefetch, watch } from 'vue'
import { useRoute } from 'vue-router'

import { AppFaqItem } from '/@app'
import { IntlMsg, type PublicPageContext } from '/@shared/app'

import type { RouteNamedMap } from '/@/router'
import {
  useDedicatedStore,
  useHostingStore,
  usePagesStore,
  useVPSStore,
} from '/@/stores'
import { useI18n, type IntlMsgKey } from '/@/i18n'

import img2 from '/@/assets/images/products/equipment/2.webp'

const pagesStore = usePagesStore()
const dedicatedStore = useDedicatedStore()
const vpsStore = useVPSStore()
const hostingStore = useHostingStore()

const route = useRoute()

const { locale, t } = useI18n()

const footerFallbackTextKey = computed<IntlMsgKey | undefined>(() => {
  if (route.name === 'VpsLocationPage') {
    const d: Record<string, IntlMsgKey> = {
      usa: 'seo.vps-in.usa.seo-text',
      germany: 'seo.vps-in.germany.seo-text',
      netherlands: 'seo.vps-in.netherlands.seo-text',
      spain: 'seo.vps-in.spain.seo-text',
      switzerland: 'seo.vps-in.switzerland.seo-text',
      // russia
    }

    return d[route.params.slug]
  }

  if (route.name === 'DsPlansetPage') {
    const d: Record<string, IntlMsgKey> = {
      'with-discount': 'seo.dedicated-planset.with-discount.seo-text',
      nvme: 'seo.dedicated-planset.nvme.seo-text',
      'blockchain-crypto': 'seo.dedicated-planset.blockchain-crypto.seo-text',
      'windows-rdp': 'seo.dedicated-planset.windows-rdp.seo-text',
      database: 'seo.dedicated-planset.database.seo-text',
      storage: 'seo.dedicated-planset.storage.seo-text',
      'for-web-projects': 'seo.dedicated-planset.for-web-projects.seo-text',
      'for-1c': 'seo.dedicated-planset.for-1c.seo-text',
      'amd-epyc': 'seo.dedicated-planset.amd-epyc.seo-text',
      'vmware-esxi': 'seo.dedicated-planset.vmware-esxi.seo-text',
      highload: 'seo.dedicated-planset.highload.seo-text',
    }

    return d[route.params.slug]
  }

  if (route.name === 'BackupPage') return 'seo.backups.seo-text'
  if (route.name === 'DsPage') return 'seo.dedicated-servers.seo-text'
  if (route.name === 'VhPage') return 'seo.hosting.seo-text'
  if (route.name === 'VpsPage') return 'seo.vps.seo-text'

  if (route.name === 'VpsLandingPage') {
    const d: Record<string, IntlMsgKey> = {
      windows: 'seo.vps-on.windows.seo-text',
      ubuntu: 'seo.vps-on.ubuntu.seo-text',
      linux: 'seo.vps-on.linux.seo-text',
      'website-hosting': 'seo.vps-on.website-hosting.seo-text',
      databases: 'seo.vps-on.databases.seo-text',
      trading: 'seo.vps-on.trading.seo-text',
      kvm: 'seo.vps-on.kvm.seo-text',
    }

    return d[route.params.slug]
  }

  if (route.name === 'VhCmsPage') {
    const d: Record<string, IntlMsgKey> = {
      phalcon: 'seo.hosting-for.phalcon.seo-text',
      mediawiki: 'seo.hosting-for.mediawiki.seo-text',
      modx: 'seo.hosting-for.modx.seo-text',
      wordpress: 'seo.hosting-for.wordpress.seo-text',
      '1c-bitrix': 'seo.hosting-for.1c-bitrix.seo-text',
      phpbb: 'seo.hosting-for.phpbb.seo-text',
      prestashop: 'seo.hosting-for.prestashop.seo-text',
      dle: 'seo.hosting-for.dle.seo-text',
      instantcms: 'seo.hosting-for.instantcms.seo-text',
      vbulletin: 'seo.hosting-for.vbulletin.seo-text',
      magento: 'seo.hosting-for.magento.seo-text',
      opencart: 'seo.hosting-for.opencart.seo-text',
      laminas: 'seo.hosting-for.laminas.seo-text',
      'umi-cms': 'seo.hosting-for.umi-cms.seo-text',
      yii: 'seo.hosting-for.yii.seo-text',
      'cs-cart': 'seo.hosting-for.cs-cart.seo-text',
      laravel: 'seo.hosting-for.laravel.seo-text',
      simpla: 'seo.hosting-for.simpla.seo-text',
      symfony: 'seo.hosting-for.symfony.seo-text',
      joomla: 'seo.hosting-for.joomla.seo-text',
      'october-cms': 'seo.hosting-for.october-cms.seo-text',
      drupal: 'seo.hosting-for.drupal.seo-text',
      'evolution-cms': 'seo.hosting-for.evolution-cms.seo-text',
      codeigniter: 'seo.hosting-for.codeigniter.seo-text',
      hostcms: 'seo.hosting-for.hostcms.seo-text',
      'invision-community': 'seo.hosting-for.invision-community.seo-text',
      'shop-script': 'seo.hosting-for.shop-script.seo-text',
      woocommerce: 'seo.hosting-for.woocommerce.seo-text',
      xenforo: 'seo.hosting-for.xenforo.seo-text',
    }

    return d[route.params.slug]
  }

  if (route.name === 'VhLocationPage') {
    const d: Record<string, IntlMsgKey> = {
      ukraine: 'seo.hosting-in.ukraine.seo-text',
      russia: 'seo.hosting-in.russia.seo-text',
      spain: 'seo.hosting-in.spain.seo-text',
      switzerland: 'seo.hosting-in.switzerland.seo-text',
      usa: 'seo.hosting-in.usa.seo-text',
      netherlands: 'seo.hosting-in.netherlands.seo-text',
    }

    return d[route.params.slug]
  }

  return undefined
})

onServerPrefetch(() => {
  if (!footerFallbackTextKey.value) return

  return pagesStore.renderSeoConent({
    content: t(footerFallbackTextKey.value),
    destination: 'footer-fallback',
  })
})

const pagesListDict: Partial<
  Record<keyof RouteNamedMap, PublicPageContext[] | undefined>
> = {
  VpsLandingPage: vpsStore.typeLandingList,
  VpsLocationPage: vpsStore.locationLandingList,
  DsPlansetPage: dedicatedStore.planSetLandingList,
  DsLocationPage: dedicatedStore.locationLandingList,
  VhCmsPage: hostingStore.cmsLandingList,
  VhLocationPage: hostingStore.locationLandingList,
}

const seoContent = computed(() => {
  let page: PublicPageContext | undefined

  const list = pagesListDict[route.name]
  const slug = 'slug' in route.params ? route.params.slug : ''
  if (list) page = list.find(x => x.slug === slug)

  if (pagesStore.currentPageContext) page = pagesStore.currentPageContext

  return {
    faq: page?.faq,
    footer: page?.footer,
    footerFallback: footerFallbackTextKey.value
      ? t(footerFallbackTextKey.value)
      : undefined,
  }
})

const footerMarkupRender = computed(
  () =>
    pagesStore.seoFooterRenderDict[locale.value]?.[
      pagesStore.currentPublicPageSlug
    ]
)

const footerFallbackMarkupRender = computed(
  () =>
    pagesStore.fallbackSeoFooterRenderDict[locale.value]?.[
      pagesStore.currentPublicPageSlug
    ]
)

const faqMarkupRender = computed(
  () =>
    pagesStore.seoFaqRenderDict[locale.value]?.[
      pagesStore.currentPublicPageSlug
    ]
)

watch(
  seoContent,
  val => {
    const { faq, footer, footerFallback } = val

    if (footer && !footerMarkupRender.value)
      pagesStore.renderSeoConent({ content: footer, destination: 'footer' })

    if (footerFallback && !footerFallbackMarkupRender.value)
      pagesStore.renderSeoConent({
        content: footerFallback,
        destination: 'footer-fallback',
      })

    if (faq && !faqMarkupRender.value)
      pagesStore.renderSeoConent({ content: faq, destination: 'faq' })
  },
  { immediate: true }
)

const faqList = computed(() => {
  if (!faqMarkupRender.value) return []

  const _list: { question: string; answer: string[] }[] = []
  const lines = faqMarkupRender.value.split('\n')

  for (const l of lines || []) {
    if (l.slice(1, 3) === 'h2')
      _list.push({
        question: l.slice(l.indexOf('">') + 2, -5), // h2 content
        answer: [],
      })
    else _list[_list.length - 1].answer.push(l)
  }

  return _list.map(x => ({
    answer: x.answer.join('\n'),
    question: x.question,
  }))
})
</script>
